import React, { useState, useEffect, useContext } from 'react';
import { api } from './api/api';
import { AuthContext } from '../App';

const TEAM_ROLES = [
    { value: '',            label: 'All teams'   },
    { value: 'FORCE_ONE',   label: 'Force One'   },
    { value: 'BRUTE_FORCE', label: 'Brute Force' },
    { value: 'MIDLAND',     label: 'Midland'     },
    { value: 'SUPERNOVA',   label: 'Supernova'   },
    { value: 'RUSSIA',      label: 'Russia'      },
    { value: 'FRIEND',      label: 'Friend'      },
    { value: 'GUEST',       label: 'Guest'       },
    { value: 'OTHER',       label: 'Other'       },
];

// ── Columns ────────────────────────────────────────────────────────────────

const COLS = [
    { key: 'overall',         label: 'Overall',   bold: true },
    { key: 'leadership',      label: 'Lead.'                },
    { key: 'mechanics',       label: 'Mech.'                },
    { key: 'electronics',     label: 'Elec.'                },
    { key: 'aerodynamics',    label: 'Aero.'                },
    { key: 'experience',      label: 'Exp.'                 },
    { key: 'pitCoordination', label: 'PitCoord'             },
    { key: 'motivation',      label: 'Motiv.'               },
    { key: 'age',             label: 'Age'                  },
    { key: 'salary',          label: 'Salary'               },
    { key: 'racesLeft',       label: 'Races Left'           },
];

// ── Main component ─────────────────────────────────────────────────────────

const TDHistory = () => {
    const { userRole } = useContext(AuthContext);
    const isAdmin = userRole === 'ADMIN';

    const [managers,        setManagers]        = useState([]);
    const [selectedManager, setSelectedManager] = useState(null);
    const [history,         setHistory]         = useState([]);
    const [loading,         setLoading]         = useState(true);
    const [detailLoading,   setDetailLoading]   = useState(false);
    const [error,           setError]           = useState(null);
    const [teamRole,        setTeamRole]        = useState('');
    const [detailError,     setDetailError]     = useState(null);

    useEffect(() => {
        const load = async () => {
            setLoading(true);
            setError(null);
            setSelectedManager(null);
            setHistory([]);
            try {
                const url = teamRole ? `/driver-history?teamRole=${teamRole}` : '/driver-history';
                const res = await api.get(url);
                setManagers(res.data);
            } catch (err) {
                console.error(err);
                setError('Failed to load managers.');
            } finally {
                setLoading(false);
            }
        };
        load();
    }, [teamRole]);

    const selectManager = async (manager) => {
        setSelectedManager(manager);
        setDetailLoading(true);
        setDetailError(null);
        try {
            const res = await api.get(`/pit-stops/td/${manager.userId}`);
            setHistory(res.data);
        } catch (err) {
            setHistory([]);
            setDetailError(err.response?.status === 403 ? 'Access denied.' : 'Failed to load data.');
        } finally {
            setDetailLoading(false);
        }
    };

    const handleMobileSelect = (e) => {
        const m = managers.find(m => String(m.userId) === e.target.value);
        if (m) selectManager(m);
    };

    const S = {
        sidebar: { width: 220, borderRight: '1px solid var(--c-border)', overflowY: 'auto', flexShrink: 0, background: '#fff' },
        sidebarHeader: { padding: '12px 16px', fontWeight: 700, fontSize: 11, borderBottom: '1px solid var(--c-border)', background: '#fafafa', textTransform: 'uppercase', letterSpacing: '0.6px', color: 'var(--c-text-muted)' },
        managerItem: (sel) => ({ padding: '10px 16px', cursor: 'pointer', background: sel ? '#fff3f3' : 'transparent', borderLeft: `3px solid ${sel ? 'var(--c-accent)' : 'transparent'}`, fontSize: 13, fontWeight: sel ? 600 : 400, color: 'var(--c-text)', transition: 'background 0.12s' }),
        main: { flex: 1, overflowY: 'auto', padding: 24, background: 'var(--c-bg)', minWidth: 0 },
        placeholder: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', color: 'var(--c-text-light)', gap: 12 },
    };

    return (
        <div className="ra-page">
            <div style={S.sidebar} className="ra-desktop-sidebar">
                <div style={S.sidebarHeader}>Managers</div>
                {isAdmin && (
                    <div style={{ padding: '8px 12px', borderBottom: '1px solid var(--c-border)' }}>
                        <select value={teamRole} onChange={e => setTeamRole(e.target.value)}
                            style={{ width: '100%', padding: '5px 8px', borderRadius: 'var(--radius-sm)', border: '1px solid var(--c-border)', fontSize: 12, background: '#fff', color: 'var(--c-text)', cursor: 'pointer' }}>
                            {TEAM_ROLES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                        </select>
                    </div>
                )}
                {loading && <div style={{ padding: 16, color: 'var(--c-text-muted)', fontSize: 13 }}>Loading…</div>}
                {error   && <div style={{ padding: 16, color: 'var(--c-error)', fontSize: 13 }}>{error}</div>}
                {!loading && !error && managers.length === 0 && <div style={{ padding: 16, color: 'var(--c-text-muted)', fontSize: 13 }}>No data.</div>}
                {managers.map(m => (
                    <div key={m.userId} style={S.managerItem(selectedManager?.userId === m.userId)} onClick={() => selectManager(m)}>
                        {m.managerName}
                    </div>
                ))}
            </div>

            <div style={S.main} className="ra-main">
                <div className="ra-mobile-selector">
                    <select className="fb-select" style={{ width: '100%' }} value={selectedManager?.userId ?? ''} onChange={handleMobileSelect} disabled={loading}>
                        <option value="">{loading ? 'Loading…' : '— Select a manager —'}</option>
                        {managers.map(m => <option key={m.userId} value={m.userId}>{m.managerName}</option>)}
                    </select>
                </div>

                {!selectedManager && (
                    <div style={S.placeholder} className="ra-desktop-placeholder">
                        <span style={{ fontSize: 44 }}>🏗️</span>
                        <span style={{ fontSize: 14 }}>Select a manager to view TD history</span>
                    </div>
                )}

                {selectedManager && detailLoading && (
                    <div style={{ color: 'var(--c-text-muted)', marginTop: 48, textAlign: 'center', fontSize: 14 }}>Loading TD history…</div>
                )}

                {selectedManager && !detailLoading && (
                    <>
                        <div className="fb-page-header">
                            <div className="fb-page-title">{selectedManager.managerName}</div>
                            <div className="fb-page-sub">Technical Director history — {history.length} record{history.length !== 1 ? 's' : ''}</div>
                        </div>

                        {detailError ? (
                            <div style={{ color: 'var(--c-error)', fontSize: 14 }}>{detailError}</div>
                        ) : history.length === 0 ? (
                            <div style={{ color: 'var(--c-text-muted)', fontSize: 14 }}>No TD data available.</div>
                        ) : (
                            <div className="fb-card">
                                <div className="fb-table-wrap ra-table-wrap">
                                    <table className="fb-table ra-table" style={{ whiteSpace: 'nowrap' }}>
                                        <thead>
                                            <tr>
                                                <th style={{ minWidth: 90 }}>Race</th>
                                                <th style={{ minWidth: 170 }}>Name</th>
                                                {COLS.map(c => (
                                                    <th key={c.key} style={{ minWidth: 68, textAlign: 'center' }}>{c.label}</th>
                                                ))}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {history.map(entry => (
                                                <tr key={entry.id}>
                                                    <td style={{ fontWeight: 600, fontSize: 12 }}>S{entry.season} R{entry.raceNumber}</td>
                                                    <td style={{ fontSize: 12 }}>{entry.tdName ?? '—'}</td>
                                                    {COLS.map(c => (
                                                        <td key={c.key} style={{ textAlign: 'center', fontWeight: c.bold ? 700 : 400 }}>
                                                            {c.fmt ? c.fmt(entry[c.key]) : (entry[c.key] ?? '—')}
                                                        </td>
                                                    ))}
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default TDHistory;
